import { AfterViewInit, Component, OnInit } from '@angular/core';

import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  BreadcrumbsService,
  IPathArray,
} from '../../shared/services/breadcrumbs.service';
import { RippleModule } from 'primeng/ripple';
import { FormsModule } from '@angular/forms';
import { GalleryComponent } from '../../shared/components/gallery/gallery.component';
import { PriceAndActionsBlockComponent } from '../../shared/components/product/price-and-actions-block/price-and-actions-block.component';
import { AskAboutProductComponent } from '../../shared/components/ask-about-product/ask-about-product.component';
import { SimilarProductsComponent } from '../../shared/components/similar-products/similar-products.component';
import { ProductDetailsHeaderComponent } from '../../shared/components/product/product-details-header/product-details-header.component';
import { ProductDetailsDescriptionComponent } from '../../shared/components/product/product-details-description/product-details-description.component';
import { ProductDetailsParamsComponent } from '../../shared/components/product/product-details-params/product-details-params.component';
import {
  Collection,
  CollectionBreadcrumb,
  GetProductDetailQuery,
  GetProductDetailQueryVariables,
  Product,
  ProductVariant,
  SearchResult,
} from '../../graphql/generated/generated-types';
import { Platform } from '@angular/cdk/platform';
import { NotAvailableOverlayComponent } from '../../shared/components/not-available-overlay/not-available-overlay.component';
import { StateService } from '../../core/state/old/state.service';
import { ActivatedRoute } from '@angular/router';
import {
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Observable, Subject, merge } from 'rxjs';
import { notNullOrUndefined } from '../../shared/utils/not-null-or-undefined';
import { DataService } from '../../core/providers/data/data.service';
import { GET_PRODUCT_DETAIL } from './product-detail.graphql';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Meta, Title } from '@angular/platform-browser';
import { PageDescriptionComponent } from '../../shared/components/page-description/page-description.component';
import { VehicleTypeService } from '../../shared/services/vehicle-type.service';
import { SelectedModificationService } from '../../shared/services/selected-modification.service';

@Component({
  selector: 'app-product',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbsComponent,
    AskAboutProductComponent,
    GalleryComponent,
    SimilarProductsComponent,
    PriceAndActionsBlockComponent,
    RippleModule,
    ProductDetailsHeaderComponent,
    ProductDetailsDescriptionComponent,
    ProductDetailsParamsComponent,
    NotAvailableOverlayComponent,
    ProgressSpinnerModule,
    PageDescriptionComponent,
  ],
  templateUrl: './product.component.html',
  styleUrl: './product.component.scss',
})
export class ProductComponent implements OnInit {
  public initialLoading: boolean = true;
  public loading$: Observable<boolean>;

  product!: Product;
  selectedAsset: { id: string; preview: string } | undefined;
  selectedVariant: ProductVariant;
  private unsubscribe$ = new Subject<void>();

  shippingMethods: SearchResult[] = [];

  public studdedTires: boolean;
  public studdableTires: boolean;

  constructor(
    private breadcrumbsService: BreadcrumbsService,
    private platform: Platform,
    private stateService: StateService,
    private route: ActivatedRoute,
    private dataService: DataService,
    private metaService: Meta,
    private titleService: Title,
    private vehicleTypeService: VehicleTypeService,
    public selectedModificationService: SelectedModificationService
  ) {}

  ngOnInit(): void {
    if (this.platform.isBrowser) {
      window.scrollTo({ top: 0 });
    }
    this.getProduct();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public hasStuddableTireAttribute(product: Product) {
    if (product?.customAttributes?.productAttributes) {
      const studdableTire = product.customAttributes.productAttributes.some(
        (attribute: { code: string; value: string }) =>
          attribute.code === 'tire-studdable' && attribute.value === 'Yes'
      );
      this.studdableTires = studdableTire;
    } else {
      this.studdableTires = false;
    }
  }

  public hasStuddedTyreAttribute(product: Product) {
    if (product?.customAttributes?.productAttributes) {
      const studdableTire = product.customAttributes.productAttributes.some(
        (attribute: { code: string; value: string }) =>
          attribute.code === 'tire-studded' && attribute.value === 'Yes'
      );
      this.studdedTires = studdableTire;
    } else {
      this.studdedTires = false;
    }
  }

  private getProduct() {
    const lastCollectionSlug$ = this.stateService.select(
      (state) => state.lastCollectionSlug
    );
    const productSlug$ = this.route.paramMap.pipe(
      map((paramMap) => paramMap.get('productSlug')),
      filter(notNullOrUndefined),
      takeUntil(this.unsubscribe$)
    );

    const product$ = productSlug$.pipe(
      switchMap((slug) => {
        return this.dataService.query<
          GetProductDetailQuery,
          GetProductDetailQueryVariables
        >(
          GET_PRODUCT_DETAIL,
          {
            slug,
          },
          'network-only'
        );
      }),
      tap((data) => {
        this.hasStuddableTireAttribute(data.product as Product);
        this.hasStuddedTyreAttribute(data.product as Product);
      }),
      map((data) => data.product as Product),
      filter(notNullOrUndefined),
      withLatestFrom(lastCollectionSlug$),
      takeUntil(this.unsubscribe$)
    );

    product$.subscribe(([product, lastCollectionSlug]) => {
      this.product = product;
      this.selectedVariant = product.variants[0];
      if (this.product.featuredAsset) {
        this.selectedAsset = this.product.featuredAsset;
      } else {
        this.selectedAsset = undefined;
      }

      this.updateSeo();

      const collection = this.getMostRelevantCollection(
        product.collections,
        lastCollectionSlug
      );
      this.initialLoading = false;
      const breadcrumbs = collection ? collection.breadcrumbs : [];
      this.setBreadcrumbs(breadcrumbs);
    });

    this.loading$ = merge(
      productSlug$.pipe(map(() => true)),
      product$.pipe(map(() => false))
    );
  }

  private updateSeo() {
    const productName = this.product.name;
    const productDescription =
      this.product.description ||
      'Discover detailed information about our premium auto parts.';
    const productKeywords = `product details, auto parts, product specifications, customer reviews, Fastparts, ${productName}`;
    const productTitle = `${productName} | Fastparts`;

    this.metaService.updateTag({
      name: 'description',
      content: productDescription,
    });
    this.metaService.updateTag({ name: 'keywords', content: productKeywords });
    this.titleService.setTitle(productTitle);

    this.metaService.updateTag({
      property: 'og:description',
      content: productDescription,
    });
    this.metaService.updateTag({
      name: 'twitter:description',
      content: productDescription,
    });
    this.metaService.updateTag({ property: 'og:title', content: productTitle });
    this.metaService.updateTag({
      name: 'twitter:title',
      content: productTitle,
    });

    const featuredImage = this.product.featuredAsset
      ? this.product.featuredAsset.preview
      : 'https://dev.shop.fastparts.is/media/hero-lg-YN3VJ6A7.png';
    this.metaService.updateTag({ name: 'og:image', content: featuredImage });
    this.metaService.updateTag({
      name: 'twitter:image',
      content: featuredImage,
    });
  }

  private getMostRelevantCollection(
    collections: Collection[],
    lastCollectionSlug: string | null
  ) {
    const lastCollection = collections.find(
      (c) => c.slug === lastCollectionSlug
    );
    if (lastCollection) {
      return lastCollection;
    }
    return collections.slice().sort((a, b) => {
      if (a.breadcrumbs.length < b.breadcrumbs.length) {
        return 1;
      }
      if (a.breadcrumbs.length > b.breadcrumbs.length) {
        return -1;
      }
      return 0;
    })[0];
  }

  private setBreadcrumbs(breadcrumbs: CollectionBreadcrumb[] | any[]) {
    let formattedBreadcrumbs: IPathArray[] | any = [];
    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      if ('slug' in breadcrumbs[0]) {
        const mappedBreadcrumbs = breadcrumbs
          .map((breadcrumb, index) => ({
            label: breadcrumb.name,
            routerLink: `/${this.vehicleTypeService.currentVehicleType}/shop/category/${breadcrumb.slug}`,
            queryParams: {
              modificationId: this.selectedModificationService?.selectedModificationId,
            },
            arrow: true,
          }))
          .filter(
            (breadcrumb, index) =>
              !(index === 0 && breadcrumb.label === '__root_collection__')
          );
        formattedBreadcrumbs = [
          {
            label: 'Parts catalog',
            routerLink: `/${this.vehicleTypeService?.currentVehicleType}/parts-catalog`,
            queryParams: {modificationId: this.selectedModificationService?.selectedModificationId},
            arrow: breadcrumbs.length > 0, // Set arrow based on whether there are other breadcrumbs
          },
          ...mappedBreadcrumbs, // Combine with the mapped breadcrumbs
          {
            label: this.product.name,
            routerLink: `/${this.vehicleTypeService.currentVehicleType}/shop/category/${
              breadcrumbs[breadcrumbs?.length - 1].slug
            }/${this.product.slug}`,
            queryParams: {
              modificationId: this.selectedModificationService?.selectedModificationId,
            },
            arrow: false,
          },
        ];
      }
    } else {
      // Case: Unknown Format
      this.breadcrumbsService.setBreadcrumbs([
        {
          label: 'Unknown',
          routerLink: '/',
          arrow: false,
        },
      ]);
    }
    this.breadcrumbsService.setBreadcrumbs(formattedBreadcrumbs);
  }


}
